import { FilterSidebar } from "@/components/layout/FilterSidebar";
import { NewSearchBar } from "@/components/layout/NewSearchBar";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader } from "@/components/layout/PageHeader";
import Seo from "@/components/seo/Seo";
import { HelpRequestButton } from "@/components/HelpRequestButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { toArray } from "@/lib/array";
import { normalizeImageUrl } from "@/lib/image-url";
import { trackEvent } from "@/lib/analytics";
import { buildGoogleMapsDirectionsUrl } from "@/lib/google-maps-links";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Business,
  businessCategories,
  verificationTypes,
} from "@shared/schema";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import {
  Award,
  Bookmark,
  CheckCircle,
  CheckCircle2,
  Filter,
  Heart,
  Info,
  Lock,
  Mail,
  MapPinned,
  Navigation,
  MessageSquare,
  Phone,
  Plus,
  Shield,
  ShoppingBag,
  Star,
  TrendingUp,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { koreanRestaurantMenuTypes } from "@shared/business-food-tags";
import {
  getBusinessPlacementLevel,
  rankBusinessPlacements,
} from "@shared/business-placement";
import { getCanonicalSuburbName } from "@shared/suburbs";

const regions = [
  { value: "ALL", label: "전체" },
  { value: "NSW", label: "시드니 (NSW)" },
  { value: "VIC", label: "멜번 (VIC)" },
  { value: "QLD", label: "브리즈번/케언즈 (QLD)" },
  { value: "SA", label: "아델레이드 (SA)" },
  { value: "WA", label: "퍼스 (WA)" },
  { value: "NT", label: "다윈 (NT)" },
  { value: "ACT", label: "캔버라 (ACT)" },
];

type BusinessPage = {
  items: DirectoryBusiness[];
  nextCursor: string | null;
};

type DirectoryBusiness = Business & {
  hasActiveAdvertisement?: boolean;
};

function DirectoryBusinessCard({
  business,
  categoryLabel,
  city,
  position,
  onOpen,
}: {
  business: DirectoryBusiness;
  categoryLabel: string;
  city: string;
  position: number;
  onOpen: () => void;
}) {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const name = business.businessNameKo || business.businessName;
  const placementLevel = getBusinessPlacementLevel(business);
  const image = normalizeImageUrl(business.coverImageUrl || business.logoUrl);

  // 찜하기 상태 확인 (MyPage.tsx "업체" 탭이 실제로 채워지려면 이 카드에서
  // "business" 타입 북마크를 생성할 수 있어야 함 - 기존에는 article에서만 가능했음)
  const { data: bookmarkData } = useQuery<{ isBookmarked: boolean; bookmarkId?: string }>({
    queryKey: ["/api/bookmarks/check", user?.uid, "business", business.id],
    queryFn: async () => {
      const params = new URLSearchParams({
        userId: user?.uid || "",
        itemType: "business",
        itemId: String(business.id ?? ""),
      });
      const res = await fetch(`/api/bookmarks/check?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to check bookmark");
      return res.json();
    },
    enabled: isAuthenticated && !!user,
  });
  const isBookmarked = bookmarkData?.isBookmarked || false;

  const addBookmarkMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("로그인이 필요합니다");
      return await apiRequest("POST", "/api/bookmarks", {
        userId: user.uid,
        itemType: "business",
        itemId: business.id,
        itemTitle: name,
        itemImageUrl: image || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks/check", user?.uid, "business", business.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks", user?.uid] });
      toast({ title: "찜하기 완료", description: "마이페이지에서 확인하실 수 있습니다" });
    },
    onError: (error: any) => {
      toast({ title: "찜하기 실패", description: error.message, variant: "destructive" });
    },
  });

  const removeBookmarkMutation = useMutation({
    mutationFn: async () => {
      if (!bookmarkData?.bookmarkId) throw new Error("북마크 ID를 찾을 수 없습니다");
      return await apiRequest("DELETE", `/api/bookmarks/${bookmarkData.bookmarkId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks/check", user?.uid, "business", business.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks", user?.uid] });
      toast({ title: "찜하기 취소", description: "찜하기가 해제되었습니다" });
    },
    onError: (error: any) => {
      toast({ title: "찜하기 취소 실패", description: error.message, variant: "destructive" });
    },
  });

  const handleBookmarkToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isBookmarked) {
      removeBookmarkMutation.mutate();
    } else {
      addBookmarkMutation.mutate();
    }
  };

  const isBookmarkPending = addBookmarkMutation.isPending || removeBookmarkMutation.isPending;
  // averageRating/reviewCount are now real aggregates from business_reviews
  // (functions/lib/business-review-stats.ts) instead of the hardcoded "0"/0
  // the list API used to return. Only show the rating when there is at least
  // one real review - no "0.0 · (0)" badges for businesses nobody has reviewed.
  const reviewCount = Number((business as any).reviewCount || 0);
  const averageRating = Number((business as any).averageRating || 0);
  const mapUrl = buildGoogleMapsDirectionsUrl({
    name: business.businessName || name,
    address: business.address,
    latitude: business.latitude,
    longitude: business.longitude,
  });
  const track = (event: "phone_click" | "directions_click") => {
    void trackEvent({ event, contentType: "business", contentId: business.id });
  };

  return (
    <Card
      className="overflow-hidden border-border/80 shadow-sm transition-shadow hover:shadow-md"
      data-business-impression={business.id}
      data-business-position={position}
      data-business-placement={placementLevel === "advertiser" ? "sponsored" : "organic"}
    >
      <CardContent className="p-3">
        <div className="flex gap-3">
          <div className="h-20 w-20 flex-none overflow-hidden rounded-xl border bg-muted sm:h-24 sm:w-24">
            {image ? (
              <img src={image} alt="" className="h-full w-full object-cover" loading="lazy" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xl font-bold text-muted-foreground">
                {name?.trim().charAt(0) || "O"}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h2 className="line-clamp-1 text-base font-bold leading-5 sm:text-lg">{name}</h2>
              <div className="flex flex-none flex-wrap items-start justify-end gap-1">
                {placementLevel === "advertiser" && <Badge>광고</Badge>}
                {placementLevel === "premium" && <Badge variant="outline">프리미엄</Badge>}
                {business.isVerified && <Badge className="bg-emerald-600">업체 확인</Badge>}
                {isAuthenticated && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-6 w-6 ${isBookmarked ? "text-primary" : "text-muted-foreground"}`}
                    onClick={handleBookmarkToggle}
                    disabled={isBookmarkPending}
                    data-testid={`button-bookmark-business-${business.id}`}
                  >
                    <Bookmark className={`h-3.5 w-3.5 ${isBookmarked ? "fill-current" : ""}`} />
                  </Button>
                )}
              </div>
            </div>
            <p className="mt-0.5 truncate text-xs text-muted-foreground sm:text-sm">
              {[categoryLabel, city].filter(Boolean).join(" · ") || "업체 정보"}
            </p>
            {reviewCount > 0 && (
              <p className="mt-0.5 flex items-center gap-1 text-xs sm:text-sm">
                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                <span className="font-semibold">{averageRating.toFixed(1)}</span>
                <span className="text-muted-foreground">리뷰 {reviewCount}개</span>
              </p>
            )}
            {business.shortDescription && (
              <p className="mt-1 line-clamp-2 text-xs text-muted-foreground sm:text-sm">{business.shortDescription}</p>
            )}
          </div>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-1.5">
          {business.contactPhone ? (
            <Button variant="outline" size="sm" asChild className="min-h-11 sm:min-h-9">
              <a href={`tel:${business.contactPhone}`} onClick={() => track("phone_click")}>
                <Phone className="mr-1 h-3.5 w-3.5" />전화
              </a>
            </Button>
          ) : (
            <Button variant="outline" size="sm" disabled className="min-h-11 sm:min-h-9"><Phone className="mr-1 h-3.5 w-3.5" />전화</Button>
          )}
          {mapUrl ? (
            <Button variant="outline" size="sm" asChild className="min-h-11 sm:min-h-9">
              <a href={mapUrl} target="_blank" rel="noreferrer" onClick={() => track("directions_click")}>
                <Navigation className="mr-1 h-3.5 w-3.5" />길찾기
              </a>
            </Button>
          ) : (
            <Button variant="outline" size="sm" disabled className="min-h-11 sm:min-h-9"><Navigation className="mr-1 h-3.5 w-3.5" />길찾기</Button>
          )}
          <Button size="sm" onClick={onOpen} className="min-h-11 sm:min-h-9">상세</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function BusinessDirectory() {
  const { user, membershipLevel, isAuthenticated } = useAuth();
  const { selectedRegion: regionContextRegion } = useRegion();
  const [, setLocation] = useLocation();
  const initialSearchQuery = new URLSearchParams(window.location.search).get("q")?.trim() ?? "";
  const [searchTerm, setSearchTerm] = useState(initialSearchQuery);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    const category = new URLSearchParams(window.location.search).get("category");
    return category && businessCategories.some((item) => item.id === category)
      ? category
      : "all";
  });
  const [selectedFoodTag, setSelectedFoodTag] = useState(() => {
    const food = new URLSearchParams(window.location.search).get("food");
    return food && koreanRestaurantMenuTypes.some((item) => item.id === food)
      ? food
      : "all";
  });
  const requestedRegion = new URLSearchParams(window.location.search).get("region");
  const requestedSuburb = new URLSearchParams(window.location.search).get("suburb");
  const [selectedRegion, setSelectedRegion] = useState(() =>
    requestedRegion && regions.some((region) => region.value === requestedRegion)
      ? requestedRegion
      : regionContextRegion || "ALL",
  );
  const [selectedSuburbFilter, setSelectedSuburbFilter] = useState<string | undefined>(() =>
    getCanonicalSuburbName(selectedRegion, requestedSuburb),
  );
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showAllRegular, setShowAllRegular] = useState(false);

  const pageSize = 24;
  const {
    data: businessPages,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<BusinessPage>({
    queryKey: ["/api/business-directory", selectedRegion, selectedSuburbFilter, selectedCategory, selectedFoodTag, searchQuery],
    initialPageParam: "",
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (selectedRegion !== "ALL") {
        params.set("region", selectedRegion);
        console.log("[BusinessDirectory] Filtering by region:", selectedRegion);
      }
      if (selectedSuburbFilter) params.set("suburb", selectedSuburbFilter);
      if (selectedCategory && selectedCategory !== "all") {
        params.set("category", selectedCategory);
        console.log("[BusinessDirectory] Filtering by category:", selectedCategory);
      }
      if (selectedCategory === "restaurant" && selectedFoodTag !== "all") {
        params.set("food", selectedFoodTag);
      }
      if (searchQuery.trim()) params.set("q", searchQuery.trim());
      // status 파라미터 제거 - D1 스키마에 status 필드가 없고 API에서 필터링하지 않음
      params.set("limit", String(pageSize));
      if (pageParam) params.set("cursor", String(pageParam));
      console.log("[BusinessDirectory] API URL:", `/api/business-directory?${params}`);
      const response = await fetch(`/api/business-directory?${params}`);
      if (!response.ok) throw new Error("Failed to fetch businesses");
      const data = await response.json();
      // API 응답 형식 처리: { ok: true, data: [...] } 또는 직접 배열
      const items = Array.isArray(data) ? data : data?.data || data?.items || [];
      return {
        items,
        nextCursor: response.headers.get("X-Next-Cursor") || null,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
  });

  const rawBusinesses = businessPages?.pages.flatMap((page) => page.items) ?? [];

  // Safe array conversion - prevents ".filter() is not a function" errors
  const businesses = toArray<DirectoryBusiness>(rawBusinesses);

  const filteredBusinesses = businesses;

  // 찜한 업체를 목록 상단에 노출하기 위한 북마크 조회. DirectoryBusinessCard의
  // 찜하기/찜해제 mutation이 이미 ["/api/bookmarks", user?.uid] 키를 무효화하고
  // 있어서(handleBookmarkToggle), 같은 키를 쓰면 카드에서 찜 상태를 바꾸는 즉시
  // 이 목록도 자동으로 다시 정렬된다.
  const { data: myBookmarks } = useQuery<{ itemType: string; itemId: string }[]>({
    queryKey: ["/api/bookmarks", user?.uid],
    queryFn: async () => {
      const res = await fetch(`/api/bookmarks?userId=${encodeURIComponent(user!.uid)}`);
      if (!res.ok) throw new Error("Failed to fetch bookmarks");
      return res.json();
    },
    enabled: isAuthenticated && !!user,
  });
  const bookmarkedBusinessIds = useMemo(
    () =>
      new Set(
        (myBookmarks || [])
          .filter((bookmark) => bookmark.itemType === "business")
          .map((bookmark) => String(bookmark.itemId)),
      ),
    [myBookmarks],
  );

  // Legacy Platinum is presented and ranked as the unified Premium plan.
  // 찜한 업체를 각 노출 등급(광고/프리미엄/일반) 안에서 최우선으로, 그 다음
  // createdAt 기준으로 정렬 (최신순). 유료 노출 등급 자체를 찜하기로 뛰어넘지는
  // 않도록 rankBusinessPlacements가 등급별로 다시 묶는다.
  const sortedBusinesses = rankBusinessPlacements(
    [...filteredBusinesses].sort((a, b) => {
      const aBookmarked = bookmarkedBusinessIds.has(String(a.id)) ? 1 : 0;
      const bBookmarked = bookmarkedBusinessIds.has(String(b.id)) ? 1 : 0;
      if (aBookmarked !== bBookmarked) return bBookmarked - aBookmarked;
      const dateA = new Date(a.createdAt || 0).getTime();
      const dateB = new Date(b.createdAt || 0).getTime();
      return dateB - dateA; // 같은 노출 등급 안에서는 최신순 (DESC)
    }),
  );

  // Platinum: Group by category and take max 3 per category (최신순으로 정렬)
  const platinumFiltered = sortedBusinesses.filter(
    (b) => b.membershipTier === "platinum"
  );
  // Group by category and take max 3 per category (최신순 유지)
  const platinumByCategory = platinumFiltered.reduce((acc, business) => {
    const category = business.category || "uncategorized";
    if (!acc[category]) {
      acc[category] = [];
    }
    if (acc[category].length < 3) {
      acc[category].push(business);
    }
    return acc;
  }, {} as Record<string, Business[]>);
  const platinumBusinesses = Object.values(platinumByCategory).flat();

  const premiumBusinesses = sortedBusinesses.filter(
    (b) => b.membershipTier === "premium" || b.membershipTier === "platinum"
  );
  const regularBusinesses = sortedBusinesses.filter(
    (b) =>
      b.membershipTier === "silver" ||
      b.membershipTier === "free" ||
      !b.membershipTier
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
          const element = entry.target as HTMLElement;
          const businessId = element.dataset.businessImpression;
          if (!businessId) continue;
          void trackEvent({
            event: "business_impression",
            contentType: "business",
            contentId: businessId,
            searchQuery: searchTerm || undefined,
            position: Number(element.dataset.businessPosition || 0),
            placementType: element.dataset.businessPlacement === "sponsored" ? "sponsored" : "organic",
            region: selectedRegion === "ALL" ? undefined : selectedRegion,
          });
          observer.unobserve(element);
        }
      },
      { threshold: 0.5 }
    );
    const elements = document.querySelectorAll<HTMLElement>("[data-business-impression]");
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [filteredBusinesses, searchTerm, selectedRegion, selectedCategory, showAllRegular]);

  const getCategoryLabel = (categoryId: string) => {
    if (!categoryId || categoryId === "all") return "";
    return (
      businessCategories.find((c) => c.id === categoryId)?.label || categoryId
    );
  };

  const getBusinessCategoryLabel = (business: Business) => {
    const categoryLabel = getCategoryLabel(business.category);
    if (business.category !== "restaurant") return categoryLabel;
    const tags = Array.isArray((business as Business & { foodTags?: string[] }).foodTags)
      ? (business as Business & { foodTags?: string[] }).foodTags || []
      : [];
    const labels = tags
      .map((tag) => koreanRestaurantMenuTypes.find((item) => item.id === tag)?.shortLabel)
      .filter(Boolean);
    return labels.length > 0 ? labels.join(" · ") : categoryLabel;
  };

  useEffect(() => {
    if (selectedCategory !== "restaurant" && selectedFoodTag !== "all") {
      setSelectedFoodTag("all");
    }
  }, [selectedCategory, selectedFoodTag]);

  // Get city name only (remove state/region)
  const getCityName = (business: Business) => {
    if (business.city) return business.city;
    // Extract city from region if available
    const regionLabel = regions.find((r) => r.value === business.region)?.label;
    if (regionLabel && regionLabel.includes("시드니")) return "시드니";
    if (regionLabel && regionLabel.includes("멜버른")) return "멜버른";
    if (regionLabel && regionLabel.includes("브리즈번")) return "브리즈번";
    if (regionLabel && regionLabel.includes("퍼스")) return "퍼스";
    if (regionLabel && regionLabel.includes("애들레이드")) return "애들레이드";
    // Fallback to region label if no city match
    return regionLabel || business.region || "";
  };

  // Get membership tier badge color
  const getTierBadgeColor = (tier: string | null) => {
    switch (tier) {
      case "platinum":
      case "premium":
        return "bg-gradient-to-r from-purple-400 to-purple-600 text-white";
      case "free":
        return "bg-muted text-muted-foreground";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  // Check if user can view business details based on membership
  const canViewDetails = (business: Business) => {
    if (!isAuthenticated) return false; // Guest: search only
    if (membershipLevel === "guest") return false;
    return true; // Free and above can view
  };

  // Check if user can access premium features
  const canAccessPremiumFeatures = () => {
    return membershipLevel === "premium" || membershipLevel === "platinum";
  };

  // Check if user can view email (Premium and above only)
  const canViewEmail = () => {
    return membershipLevel === "premium" || membershipLevel === "platinum";
  };

  // Handle premium feature click
  const handlePremiumFeatureClick = (featureName: string) => {
    if (!canAccessPremiumFeatures()) {
      setUpgradeMessage(
        `"${featureName}" 기능은 프리미엄 회원 전용입니다. 업그레이드하고 더 많은 정보를 확인하세요!`
      );
      setUpgradeDialogOpen(true);
    }
  };

  // Get verification badges for a business
  const getVerificationBadges = (business: Business) => {
    const badges = [];

    if (business.isPartnerVerified) {
      const partnerVerification = verificationTypes.find(
        (v) => v.id === "partner"
      );
      badges.push({ ...partnerVerification, priority: 3 });
    }
    if (business.isPremiumVerified) {
      const premiumVerification = verificationTypes.find(
        (v) => v.id === "premium"
      );
      badges.push({ ...premiumVerification, priority: 2 });
    }
    if (business.isVerified) {
      const basicVerification = verificationTypes.find((v) => v.id === "basic");
      badges.push({ ...basicVerification, priority: 1 });
    }

    return badges;
  };

  // Render verification icon based on type
  const renderVerificationIcon = (
    iconName: string,
    color?: string,
    isVerified: boolean = true
  ) => {
    const className = `h-4 w-4 ${
      !isVerified
        ? "text-gray-400"
        : color === "green"
        ? "text-green-600"
        : color === "blue"
        ? "text-[#663366]"
        : "text-amber-600"
    }`;

    switch (iconName) {
      case "CheckCircle2":
        return <CheckCircle2 className={className} />;
      case "Star":
        return <Star className={className} />;
      case "Shield":
        return <Shield className={className} />;
      default:
        return null;
    }
  };

  // Render all verification icons (gray if not verified, colored if verified)
  const renderAllVerificationIcons = (business: Business) => {
    return verificationTypes.map((verification) => {
      const isVerified = !!(
        (verification.id === "basic" && business.isVerified) ||
        (verification.id === "premium" && business.isPremiumVerified) ||
        (verification.id === "partner" && business.isPartnerVerified)
      );

      return (
        <Tooltip key={verification.id}>
          <TooltipTrigger asChild>
            <span
              className="cursor-help inline-block"
              data-testid={`badge-verification-${verification.id}-${business.id}`}
            >
              {renderVerificationIcon(
                verification.icon,
                verification.color,
                isVerified
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="font-semibold mb-1">{verification.nameKo}</p>
            <p className="text-xs">{verification.description}</p>
            {!isVerified && (
              <p className="text-xs text-muted-foreground mt-1">
                * 프리미엄 업체만 인증 획득 가능
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      );
    });
  };


  return (
    <>
      <Seo
        title="교민 업체주소록 — OZKNEWS"
        description="호주 한인 비즈니스 디렉토리. 신뢰할 수 있는 교민 업체 정보를 한눈에 확인하세요."
        path="/business-directory"
        keywords={[
          "교민 업체",
          "비즈니스 디렉토리",
          "한인 업체",
          "업체 주소록",
          "호주 한인",
        ]}
      />

      <div className="min-h-screen bg-background">
        {/* Search Section - NewSearchBar 사용 */}
        <NewSearchBar
          defaultCategory="all"
          categories={businessCategories.map((c) => ({
            id: c.id,
            label: c.label,
          }))}
          onSearch={(query) => {
            setSearchQuery(query);
            setSearchTerm(query);
          }}
        />

        {/* Main Content */}
        <PageContainer size="default">
          <div className="space-y-5">
            <div className="text-xs text-muted-foreground">홈 &gt; 교민업체</div>
            <PageHeader title="교민 업체주소록" description="지역과 업종으로 필요한 업체를 빠르게 찾고 바로 연락하세요." actions={isAuthenticated ? <Button onClick={() => setLocation("/business-directory/register")} data-testid="button-register-business" size="sm"><Plus className="mr-1 h-4 w-4" />업체 등록</Button> : undefined} />

            {selectedCategory === "restaurant" && (
              <div className="-mx-1 overflow-x-auto px-1 pb-1" aria-label="식당 메뉴 유형">
                <div className="flex min-w-max gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={selectedFoodTag === "all" ? "default" : "outline"}
                    onClick={() => setSelectedFoodTag("all")}
                  >
                    전체 식당
                  </Button>
                  {koreanRestaurantMenuTypes.map((type) => (
                    <Button
                      key={type.id}
                      type="button"
                      size="sm"
                      variant={selectedFoodTag === type.id ? "default" : "outline"}
                      onClick={() => setSelectedFoodTag(type.id)}
                    >
                      {type.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Membership Info Banner */}
            {!isAuthenticated && (
              <div className="bg-[#60a6fa]/10 border border-[#60a6fa]/20 rounded-lg p-4 mb-6">
                <p className="text-sm text-[#CCCCCC]">
                  전화와 길찾기는 로그인 없이 이용할 수 있습니다. 업체주는 로그인 후 기존 업체를 찾아 소유권을 요청할 수 있습니다.
                </p>
              </div>
            )}

            {/* Icon Guide */}
            <div className="hidden">
              <div className="flex items-center gap-2 mb-3">
                <Info className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-sm">아이콘 안내</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>전화번호</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>이메일</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span>사업체 확인</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Star className="h-4 w-4 text-[#663366]" />
                  <span>만족도 검증</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Shield className="h-4 w-4 text-amber-600" />
                  <span>파트너십</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Award className="h-4 w-4 text-yellow-600" />
                  <span>프리미엄</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <ShoppingBag className="h-4 w-4 text-orange-600" />
                  <span>핫샵&딜</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPinned className="h-4 w-4 text-[#663366]" />
                  <span>지도 위치</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
              <div className="flex items-center gap-3">
                {/* Mobile Filter Toggle Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMobileFilters(!showMobileFilters)}
                  className="lg:hidden"
                  data-testid="button-mobile-filter-toggle"
                >
                  <Filter className="w-4 h-4 mr-1" />
                  필터
                </Button>
              </div>
            </div>

            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
              <SheetContent side="bottom" className="max-h-[82vh] overflow-y-auto rounded-t-2xl">
                <SheetHeader className="text-left">
                  <SheetTitle>업체 필터</SheetTitle>
                  <SheetDescription>지역과 업종을 선택해 결과를 좁혀보세요.</SheetDescription>
                </SheetHeader>
                <div className="space-y-6 py-5">
                  <div>
                    <h3 className="mb-3 text-sm font-semibold">지역</h3>
                    <div className="flex flex-wrap gap-2">
                      {regions.map((region) => (
                        <Button
                          key={region.value}
                          type="button"
                          size="sm"
                          variant={selectedRegion === region.value ? "default" : "outline"}
                          onClick={() => {
                            setSelectedRegion(region.value);
                            setSelectedSuburbFilter(undefined);
                          }}
                        >
                          {region.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                  {selectedCategory === "restaurant" && (
                    <div>
                      <h3 className="mb-3 text-sm font-semibold">메뉴 유형</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {[{ id: "all", label: "전체 식당" }, ...koreanRestaurantMenuTypes].map((type) => (
                          <Button
                            key={type.id}
                            type="button"
                            variant={selectedFoodTag === type.id ? "default" : "outline"}
                            className="justify-start"
                            onClick={() => setSelectedFoodTag(type.id)}
                          >
                            {type.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <h3 className="mb-3 text-sm font-semibold">업종</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {[{ id: "all", label: "전체" }, ...businessCategories].map((category) => (
                        <Button
                          key={category.id}
                          type="button"
                          variant={selectedCategory === category.id ? "default" : "outline"}
                          className="justify-start"
                          onClick={() => setSelectedCategory(category.id)}
                        >
                          {category.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
                <SheetFooter className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedRegion("ALL");
                      setSelectedSuburbFilter(undefined);
                      setSelectedCategory("all");
                      setSelectedFoodTag("all");
                    }}
                  >
                    초기화
                  </Button>
                  <Button onClick={() => setShowMobileFilters(false)}>결과 보기</Button>
                </SheetFooter>
              </SheetContent>
            </Sheet>

            <div className="ozk-list-layout">
              {/* Sidebar - Filters (Desktop) - FilterSidebar 사용 */}
              <FilterSidebar
                sections={[
                  {
                    id: "region",
                    title: "주요 지역",
                    type: "text",
                    options: regions.map((r) => ({
                      id: r.value,
                      label: r.label,
                      value: r.value,
                    })),
                    selected: selectedRegion,
                    onSelect: (value) => {
                      setSelectedRegion(value as string);
                      setSelectedSuburbFilter(undefined);
                    },
                  },
                  {
                    id: "category",
                    title: "카테고리",
                    type: "radio",
                    options: [
                      { id: "all", label: "전체", value: "all" },
                      ...businessCategories.map((c) => ({
                        id: c.id,
                        label: c.label,
                        value: c.id,
                      })),
                    ],
                    selected: selectedCategory,
                    onSelect: (value) => setSelectedCategory(value as string),
                  },
                  ...(selectedCategory === "restaurant"
                    ? [{
                        id: "food",
                        title: "메뉴 유형",
                        type: "radio" as const,
                        options: [
                          { id: "all", label: "전체 식당", value: "all" },
                          ...koreanRestaurantMenuTypes.map((type) => ({
                            id: type.id,
                            label: type.label,
                            value: type.id,
                          })),
                        ],
                        selected: selectedFoodTag,
                        onSelect: (value: string | string[]) => setSelectedFoodTag(value as string),
                      }]
                    : []),
                ]}
                onReset={() => {
                  setSelectedRegion("ALL");
                  setSelectedSuburbFilter(undefined);
                  setSelectedCategory("all");
                  setSelectedFoodTag("all");
                }}
              />

              {/* Main Content */}
              <main className="ozk-list-main">
                {isLoading ? (
                  <div className="text-center py-12 text-muted-foreground">
                    로딩 중...
                  </div>
                ) : filteredBusinesses.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground text-lg mb-2">
                      검색 결과가 없습니다
                    </p>
                    <p className="text-sm text-muted-foreground mb-6">
                      다른 검색어나 필터를 시도해보세요. 상황을 알려주시면 OZK가 관련 업체를 찾아드릴게요.
                    </p>
                    <div className="flex justify-center">
                      <HelpRequestButton
                        sourceType="business"
                        sourceId={searchQuery || undefined}
                        zoneId={selectedRegion !== "ALL" ? selectedRegion : undefined}
                        suburb={selectedSuburbFilter || undefined}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <p className="text-muted-foreground">
                        현재{" "}
                        <span className="font-semibold text-foreground">
                          {filteredBusinesses.length}
                        </span>
                        개 업체를 표시합니다
                      </p>
                    </div>

                    <div className="space-y-3">
                      {sortedBusinesses.map((business, index) => (
                        <DirectoryBusinessCard
                          key={business.id}
                          business={business}
                          categoryLabel={getBusinessCategoryLabel(business)}
                          city={getCityName(business)}
                          position={index + 1}
                          onOpen={() => setLocation(`/business-directory/${business.id}`)}
                        />
                      ))}
                    </div>
                    {hasNextPage && (
                      <div className="mt-6 text-center">
                        <Button
                          variant="outline"
                          disabled={isFetchingNextPage}
                          onClick={() => fetchNextPage()}
                        >
                          {isFetchingNextPage ? "업체 불러오는 중..." : "업체 더 보기"}
                        </Button>
                      </div>
                    )}

                    {/* Platinum Businesses - Larger List Layout with Description & Products */}
                    {false && platinumBusinesses.length > 0 && (
                      <div className="mb-12">
                        {/* List Layout - Compact for Platinum */}
                        <div className="space-y-2">
                          {platinumBusinesses.map((business, index) => (
                            <Card
                              key={business.id}
                              className="group hover-elevate cursor-pointer transition-all duration-300 shadow-md border border-purple-200/50 dark:border-purple-800/50 bg-gradient-to-r from-purple-50/20 to-purple-50/20 dark:from-purple-950/10 dark:to-purple-950/10"
                              onClick={() =>
                                setLocation(
                                  `/business-directory/${business.id}`
                                )
                              }
                              data-testid={`card-platinum-${business.id}`}
                              data-business-impression={business.id}
                              data-business-position={index + 1}
                              data-business-placement="organic"
                            >
                              <CardContent className="p-4 py-5">
                                <div className="flex items-center gap-4">
                                  {/* Business Image - Larger thumbnail for Platinum (40-48px) */}
                                  <div className="relative flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-purple-300 dark:border-purple-700">
                                    <img
                                      src={
                                        normalizeImageUrl(
                                          business.coverImageUrl ||
                                            business.logoUrl
                                        ) ||
                                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="64"%3E%3Crect fill="%23e5e7eb" width="64" height="64"/%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="12" dy="36" dx="32" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E'
                                      }
                                      alt={
                                        business.businessNameKo ||
                                        business.businessName
                                      }
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.src =
                                          'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="64"%3E%3Crect fill="%23e5e7eb" width="64" height="64"/%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="12" dy="36" dx="32" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                                      }}
                                    />
                                    {/* Verified Badge */}
                                    {business.isVerified && (
                                      <div className="absolute -top-1 -left-1">
                                        <Badge className="bg-green-600 text-white shadow-lg text-[10px] px-1 py-0.5">
                                          <CheckCircle2 className="h-2.5 w-2.5" />
                                        </Badge>
                                      </div>
                                    )}
                                  </div>

                                  {/* Business Info - Main Content */}
                                  <div className="flex-1 min-w-0">
                                    {/* Business Name & Description */}
                                    <div className="mb-1">
                                      <h3 className="font-bold text-lg group-hover:text-primary transition-colors truncate">
                                        {business.businessNameKo ||
                                          business.businessName}
                                      </h3>
                                      {/* Location with Map Icon and Description */}
                                      <div className="flex items-center gap-2 flex-wrap mt-0.5">
                                        {getCityName(business) && (
                                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                            <MapPinned className="h-3 w-3" />
                                            <span className="truncate">
                                              {getCityName(business)}
                                            </span>
                                          </div>
                                        )}
                                        {business.shortDescription && (
                                          <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                                            {business.shortDescription}
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Rating, Category Row */}
                                    <div className="flex items-center gap-3 flex-wrap">
                                      {/* Rating */}
                                      {Number(business.averageRating) > 0 ? (
                                        <div className="flex items-center gap-1">
                                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                                          <span className="text-xs font-semibold">
                                            {Number(
                                              business.averageRating
                                            ).toFixed(1)}
                                          </span>
                                          {(business.reviewCount || 0) > 0 && (
                                            <span className="text-xs text-muted-foreground">
                                              ({business.reviewCount || 0})
                                            </span>
                                          )}
                                        </div>
                                      ) : null}

                                      {/* Category - Only show if not "all" */}
                                      {getBusinessCategoryLabel(business) && (
                                        <Badge
                                          variant="outline"
                                          className="text-xs px-2 py-0.5"
                                        >
                                          {getBusinessCategoryLabel(business)}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>

                                  {/* Contact Info - Right Side */}
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {/* Phone - Fixed width for consistent positioning */}
                                    <div className="w-[160px] flex items-center justify-end">
                                      {business.contactPhone ? (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <a
                                              href={`tel:${business.contactPhone}`}
                                              className="flex items-center gap-1.5 text-sm font-medium hover:text-primary transition-colors px-3 py-1.5 rounded-lg bg-primary/5 hover:bg-primary/10"
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                              data-testid={`link-phone-platinum-${business.id}`}
                                            >
                                              <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                                              <span className="truncate max-w-[120px]">
                                                {business.contactPhone}
                                              </span>
                                            </a>
                                          </TooltipTrigger>
                                          <TooltipContent side="top">
                                            <p className="text-xs">
                                              전화번호로 연락하기
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      ) : null}
                                    </div>

                                    {/* Action Icons */}
                                    <div className="flex items-center gap-1">
                                      {/* Email */}
                                      {canViewEmail() &&
                                      business.contactEmail ? (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <a
                                              href={`mailto:${business.contactEmail}`}
                                              className="p-1.5 hover:bg-primary/10 rounded transition-colors"
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                              data-testid={`icon-email-platinum-${business.id}`}
                                            >
                                              <Mail className="h-4 w-4 text-primary" />
                                            </a>
                                          </TooltipTrigger>
                                          <TooltipContent side="top">
                                            <p className="text-xs">
                                              {business.contactEmail}
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      ) : (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div
                                              className="p-1.5"
                                              data-testid={`icon-email-platinum-${business.id}`}
                                            >
                                              <Mail className="h-4 w-4 text-muted-foreground" />
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent side="top">
                                            <div className="text-xs space-y-1">
                                              <p>
                                                프리미엄 회원만 이메일 확인 가능
                                              </p>
                                              {!canAccessPremiumFeatures() && (
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handlePremiumFeatureClick(
                                                      "플래티넘 업체"
                                                    );
                                                  }}
                                                  className="text-primary hover:underline flex items-center gap-1 mt-1"
                                                >
                                                  <TrendingUp className="h-3 w-3" />
                                                  프리미엄으로 업그레이드
                                                </button>
                                              )}
                                            </div>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}

                                      {/* Favorite */}
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              // TODO: 북마크 기능 구현
                                            }}
                                            className="p-1.5 hover:bg-primary/10 rounded transition-colors"
                                            data-testid={`icon-favorite-platinum-${business.id}`}
                                          >
                                            <Heart className="h-4 w-4 text-muted-foreground hover:text-red-500" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                          <p className="text-xs">
                                            즐겨찾기 추가
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>

                                      {/* Review */}
                                      {(business.reviewCount || 0) > 0 && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div
                                              className="p-1.5"
                                              data-testid={`icon-review-platinum-${business.id}`}
                                            >
                                              <MessageSquare className="h-4 w-4 text-muted-foreground" />
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent side="top">
                                            <p className="text-xs">
                                              리뷰 {business.reviewCount}개
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}

                                      {/* Verification Badges */}
                                      {renderAllVerificationIcons(business)}

                                      {/* Featured */}
                                      {business.featuredUntil &&
                                        new Date(business.featuredUntil) >
                                          new Date() && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <div
                                                className="p-1.5"
                                                data-testid={`icon-featured-platinum-${business.id}`}
                                              >
                                                <Award className="h-4 w-4 text-yellow-600" />
                                              </div>
                                            </TooltipTrigger>
                                            <TooltipContent side="top">
                                              <p className="text-xs">
                                                추천 업체
                                              </p>
                                            </TooltipContent>
                                          </Tooltip>
                                        )}

                                      {/* Products */}
                                      {business.hasProducts && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div
                                              className="p-1.5"
                                              data-testid={`icon-products-platinum-${business.id}`}
                                            >
                                              <ShoppingBag className="h-4 w-4 text-orange-600" />
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent side="top">
                                            <p className="text-xs">
                                              상품 {business.productCount || 0}
                                              개
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Premium Businesses - Standard List Layout */}
                    {false && premiumBusinesses.length > 0 && (
                      <div className="mb-12">
                        {/* List Layout - Compact for Premium (Same height as Regular) */}
                        <div className="space-y-2">
                          {premiumBusinesses.map((business, index) => (
                            <Card
                              key={business.id}
                              className="group hover-elevate cursor-pointer transition-all duration-300 shadow-md border border-purple-200/50 dark:border-purple-800/50 bg-gradient-to-r from-purple-50/20 to-purple-50/20 dark:from-purple-950/10 dark:to-purple-950/10"
                              onClick={() =>
                                setLocation(
                                  `/business-directory/${business.id}`
                                )
                              }
                              data-testid={`card-premium-${business.id}`}
                              data-business-impression={business.id}
                              data-business-position={platinumBusinesses.length + index + 1}
                              data-business-placement="organic"
                            >
                              <CardContent className="p-4">
                                <div className="flex items-center gap-4">
                                  {/* Business Info - Main Content */}
                                  <div className="flex-1 min-w-0">
                                    {/* Business Name & Description */}
                                    <div className="mb-1">
                                      <h3 className="font-semibold text-base group-hover:text-primary transition-colors truncate">
                                        {business.businessNameKo ||
                                          business.businessName}
                                      </h3>
                                      {/* Location with Map Icon and Description */}
                                      <div className="flex items-center gap-2 flex-wrap mt-0.5">
                                        {getCityName(business) && (
                                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                            <MapPinned className="h-3 w-3" />
                                            <span className="truncate">
                                              {getCityName(business)}
                                            </span>
                                          </div>
                                        )}
                                        {business.shortDescription && (
                                          <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                                            {business.shortDescription}
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Rating, Category Row */}
                                    <div className="flex items-center gap-3 flex-wrap">
                                      {/* Rating */}
                                      {Number(business.averageRating) > 0 ? (
                                        <div className="flex items-center gap-1">
                                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                                          <span className="text-xs font-semibold">
                                            {Number(
                                              business.averageRating
                                            ).toFixed(1)}
                                          </span>
                                          {(business.reviewCount || 0) > 0 && (
                                            <span className="text-xs text-muted-foreground">
                                              ({business.reviewCount || 0})
                                            </span>
                                          )}
                                        </div>
                                      ) : null}

                                      {/* Category - Only show if not "all" */}
                                      {getBusinessCategoryLabel(business) && (
                                        <Badge
                                          variant="outline"
                                          className="text-xs px-2 py-0.5"
                                        >
                                          {getBusinessCategoryLabel(business)}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>

                                  {/* Contact Info - Right Side */}
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {/* Phone - Fixed width for consistent positioning */}
                                    <div className="w-[160px] flex items-center justify-end">
                                      {business.contactPhone ? (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <a
                                              href={`tel:${business.contactPhone}`}
                                              className="flex items-center gap-1.5 text-sm font-medium hover:text-primary transition-colors px-3 py-1.5 rounded-lg bg-primary/5 hover:bg-primary/10"
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                              data-testid={`link-phone-premium-${business.id}`}
                                            >
                                              <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                                              <span className="truncate max-w-[120px]">
                                                {business.contactPhone}
                                              </span>
                                            </a>
                                          </TooltipTrigger>
                                          <TooltipContent side="top">
                                            <p className="text-xs">
                                              전화번호로 연락하기
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      ) : null}
                                    </div>

                                    {/* Action Icons */}
                                    <div className="flex items-center gap-1">
                                      {/* Email */}
                                      {canViewEmail() &&
                                      business.contactEmail ? (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <a
                                              href={`mailto:${business.contactEmail}`}
                                              className="p-1.5 hover:bg-primary/10 rounded transition-colors"
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                              data-testid={`icon-email-premium-${business.id}`}
                                            >
                                              <Mail className="h-4 w-4 text-primary" />
                                            </a>
                                          </TooltipTrigger>
                                          <TooltipContent side="top">
                                            <p className="text-xs">
                                              {business.contactEmail}
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      ) : (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div
                                              className="p-1.5"
                                              data-testid={`icon-email-premium-${business.id}`}
                                            >
                                              <Mail className="h-4 w-4 text-muted-foreground" />
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent side="top">
                                            <div className="text-xs space-y-1">
                                              <p>
                                                프리미엄 회원만 이메일 확인 가능
                                              </p>
                                              {!canAccessPremiumFeatures() && (
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handlePremiumFeatureClick(
                                                      "프리미엄 업체"
                                                    );
                                                  }}
                                                  className="text-primary hover:underline flex items-center gap-1 mt-1"
                                                >
                                                  <TrendingUp className="h-3 w-3" />
                                                  프리미엄으로 업그레이드
                                                </button>
                                              )}
                                            </div>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}

                                      {/* Favorite */}
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              // TODO: 북마크 기능 구현
                                            }}
                                            className="p-1.5 hover:bg-primary/10 rounded transition-colors"
                                            data-testid={`icon-favorite-premium-${business.id}`}
                                          >
                                            <Heart className="h-4 w-4 text-muted-foreground hover:text-red-500" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                          <p className="text-xs">
                                            즐겨찾기 추가
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>

                                      {/* Review */}
                                      {(business.reviewCount || 0) > 0 && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div
                                              className="p-1.5"
                                              data-testid={`icon-review-premium-${business.id}`}
                                            >
                                              <MessageSquare className="h-4 w-4 text-muted-foreground" />
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent side="top">
                                            <p className="text-xs">
                                              리뷰 {business.reviewCount}개
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}

                                      {/* Verification Badges */}
                                      {renderAllVerificationIcons(business)}

                                      {/* Featured */}
                                      {business.featuredUntil &&
                                        new Date(business.featuredUntil) >
                                          new Date() && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <div
                                                className="p-1.5"
                                                data-testid={`icon-featured-premium-${business.id}`}
                                              >
                                                <Award className="h-4 w-4 text-yellow-600" />
                                              </div>
                                            </TooltipTrigger>
                                            <TooltipContent side="top">
                                              <p className="text-xs">
                                                추천 업체
                                              </p>
                                            </TooltipContent>
                                          </Tooltip>
                                        )}

                                      {/* Products */}
                                      {business.hasProducts && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div
                                              className="p-1.5"
                                              data-testid={`icon-products-premium-${business.id}`}
                                            >
                                              <ShoppingBag className="h-4 w-4 text-orange-600" />
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent side="top">
                                            <p className="text-xs">
                                              상품 {business.productCount || 0}
                                              개
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Free Businesses - One-Line List */}
                    {false && regularBusinesses.length > 0 && (
                      <div>
                        <div className="space-y-2">
                          {(showAllRegular ? regularBusinesses : regularBusinesses.slice(0, 30)).map((business, index) => (
                            <Card
                              key={business.id}
                              className="group hover-elevate cursor-pointer transition-all duration-300 shadow-md overflow-hidden"
                              onClick={() =>
                                setLocation(
                                  `/business-directory/${business.id}`
                                )
                              }
                              data-testid={`row-business-${business.id}`}
                              data-business-impression={business.id}
                              data-business-position={platinumBusinesses.length + premiumBusinesses.length + index + 1}
                              data-business-placement="organic"
                            >
                              <CardContent className="p-4">
                                <div className="flex items-center gap-4">
                                  {/* Business Info - Main Content */}
                                  <div className="flex-1 min-w-0">
                                    {/* Business Name */}
                                    <div className="mb-1">
                                      <h3 className="font-semibold text-base group-hover:text-primary transition-colors truncate">
                                        {business.businessNameKo ||
                                          business.businessName}
                                      </h3>
                                      {/* Location with Map Icon - No description for regular businesses */}
                                      {getCityName(business) && (
                                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                                          <MapPinned className="h-3 w-3" />
                                          <span className="truncate">
                                            {getCityName(business)}
                                          </span>
                                        </div>
                                      )}
                                    </div>

                                    {/* Rating, Category Row */}
                                    <div className="flex items-center gap-3 flex-wrap">
                                      {/* Rating */}
                                      {Number(business.averageRating) > 0 ? (
                                        <div className="flex items-center gap-1">
                                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                                          <span className="text-xs font-semibold">
                                            {Number(
                                              business.averageRating
                                            ).toFixed(1)}
                                          </span>
                                          {(business.reviewCount || 0) > 0 && (
                                            <span className="text-xs text-muted-foreground">
                                              ({business.reviewCount || 0})
                                            </span>
                                          )}
                                        </div>
                                      ) : null}

                                      {/* Category - Only show if not "all" */}
                                      {getBusinessCategoryLabel(business) && (
                                        <Badge
                                          variant="outline"
                                          className="text-xs px-2 py-0.5"
                                        >
                                          {getBusinessCategoryLabel(business)}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>

                                  {/* Contact Info - Right Side */}
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {/* Phone - Fixed width for consistent positioning */}
                                    <div className="w-[160px] flex items-center justify-end">
                                      {business.contactPhone ? (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <a
                                              href={`tel:${business.contactPhone}`}
                                              className="flex items-center gap-1.5 text-sm font-medium hover:text-primary transition-colors px-3 py-1.5 rounded-lg bg-primary/5 hover:bg-primary/10"
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                              data-testid={`link-phone-${business.id}`}
                                            >
                                              <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                                              <span className="truncate max-w-[120px]">
                                                {business.contactPhone}
                                              </span>
                                            </a>
                                          </TooltipTrigger>
                                          <TooltipContent side="top">
                                            <p className="text-xs">
                                              전화번호로 연락하기
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      ) : null}
                                    </div>

                                    {/* Action Icons */}
                                    <div className="flex items-center gap-1">
                                    {/* Email - Disabled for regular businesses */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div
                                          className="p-1.5"
                                          data-testid={`icon-email-${business.id}`}
                                        >
                                          <Mail className="h-4 w-4 text-muted-foreground opacity-50" />
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent side="top">
                                        <p className="text-xs">
                                          프리미엄 업체만 이메일 보내기 가능
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>

                                    {/* Favorite - Disabled for regular businesses */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div
                                          className="p-1.5"
                                          data-testid={`icon-favorite-${business.id}`}
                                        >
                                          <Heart className="h-4 w-4 text-muted-foreground opacity-50" />
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent side="top">
                                        <p className="text-xs">
                                          프리미엄 업체만 즐겨찾기 가능
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>

                                    {/* Review - Display only */}
                                    {(business.reviewCount || 0) > 0 && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div
                                            className="p-1.5"
                                            data-testid={`icon-review-${business.id}`}
                                          >
                                            <MessageSquare className="h-4 w-4 text-muted-foreground opacity-50" />
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                          <p className="text-xs">
                                            리뷰 {business.reviewCount || 0}개
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}

                                    {/* Verification Badges */}
                                    {renderAllVerificationIcons(business)}

                                    {/* Featured */}
                                    {business.featuredUntil &&
                                      new Date(business.featuredUntil) >
                                        new Date() && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div
                                              className="p-1.5"
                                              data-testid={`icon-featured-${business.id}`}
                                            >
                                              <Award className="h-4 w-4 text-yellow-600" />
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent side="top">
                                            <p className="text-xs">
                                              추천 업체
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}

                                    {/* Products */}
                                    {business.hasProducts && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div
                                            className="p-1.5"
                                            data-testid={`icon-products-${business.id}`}
                                          >
                                            <ShoppingBag className="h-4 w-4 text-orange-600" />
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                          <p className="text-xs">
                                            상품 {business.productCount || 0}
                                            개
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>

                        {/* 더보기 버튼 - 30개 이상인 경우 */}
                        {regularBusinesses.length > 30 && (
                          <div className="mt-6 text-center">
                            <Button
                              variant="outline"
                              onClick={() => setShowAllRegular(!showAllRegular)}
                              className="mx-auto"
                            >
                              {showAllRegular
                                ? "접기"
                                : `더보기 (${regularBusinesses.length - 30}개 더)`}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                        {false && hasNextPage && (
                      <div className="mt-8 text-center">
                        <Button
                          variant="outline"
                          disabled={isFetchingNextPage}
                          onClick={() => fetchNextPage()}
                        >
                          {isFetchingNextPage ? "업체 불러오는 중..." : `다음 ${pageSize}개 업체 불러오기`}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </main>
            </div>
          </div>
        </PageContainer>

        {/* Upgrade Dialog */}
        <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary" />
                프리미엄 기능
              </DialogTitle>
              <DialogDescription className="space-y-4">
                <p className="text-base">{upgradeMessage}</p>

                <div className="bg-primary/5 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-sm">프리미엄 회원 혜택:</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>업체 안전인증 정보 확인</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <ShoppingBag className="h-4 w-4 text-orange-600 flex-shrink-0" />
                      <span>핫샵&딜 연동 상품 보기</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <MapPinned className="h-4 w-4 text-[#663366] flex-shrink-0" />
                      <span>상세 지도 및 길찾기</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-purple-600 flex-shrink-0" />
                      <span>랭킹 차트 접근</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Award className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                      <span>추천 업체 상세 정보</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-yellow-400 fill-yellow-400 flex-shrink-0" />
                      <span>광고 제거</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-purple-500/10 to-purple-600/10 rounded-lg p-4 border border-primary/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">프리미엄</span>
                    <span className="text-2xl font-bold">
                      $9.99
                      <span className="text-sm text-muted-foreground">/월</span>
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    첫 해 특별 프로모션 가격
                  </p>
                </div>
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setUpgradeDialogOpen(false)}
                className="flex-1"
                data-testid="button-cancel-upgrade"
              >
                닫기
              </Button>
              <Button
                onClick={() => {
                  setUpgradeDialogOpen(false);
                  setLocation("/pricing");
                }}
                className="flex-1"
                data-testid="button-upgrade-now"
              >
                지금 업그레이드
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
